import { useMemo, useEffect, useState } from "react";
import axios from "axios";

// ✅ Allowed work intervals per day
const WORK_INTERVALS = [
  { from: { h: 9, m: 30 }, to: { h: 13, m: 30 } },
  { from: { h: 14, m: 0 }, to: { h: 18, m: 0 } },
];

// ✅ Clamp a session to working intervals
const clampSessionToWorkingHours = (start, end) => {
  let totalMs = 0;
  let current = new Date(start);

  const getIntervalDuration = (day, sessionStart, sessionEnd) => {
    if (day === 0) return 0; // skip Sundays
    return WORK_INTERVALS.reduce((sum, { from, to }) => {
      const intervalStart = new Date(sessionStart);
      intervalStart.setHours(from.h, from.m, 0, 0);
      const intervalEnd = new Date(sessionEnd);
      intervalEnd.setHours(to.h, to.m, 0, 0);

      const fromTime = new Date(Math.max(sessionStart.getTime(), intervalStart.getTime()));
      const toTime = new Date(Math.min(sessionEnd.getTime(), intervalEnd.getTime()));

      return fromTime < toTime ? sum + (toTime - fromTime) : sum;
    }, 0);
  };

  while (current < end) {
    const day = current.getDay(); // 0=Sun, 1=Mon...
    const nextMidnight = new Date(current);
    nextMidnight.setHours(23, 59, 59, 999);
    const sessionEnd = new Date(Math.min(end.getTime(), nextMidnight.getTime()));

    totalMs += getIntervalDuration(day, current, sessionEnd);

    // Move to next day
    current.setDate(current.getDate() + 1);
    current.setHours(0, 0, 0, 0);
  }

  return totalMs;
};

export const useWorkData = () => {
  const [tasks, setTasks] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    axios
      .get("/api/mainuser.php?action=dashboard", { withCredentials: true })
      .then((res) => setUser(res.data.user))
      .catch(() => (window.location.href = "/userlogin"));
  }, []);

  useEffect(() => {
    if (user?.username) {
      fetch(`/api/getTasksByUser.php?user=${user?.name}`)
        .then((res) => res.json())
        .then((data) => setTasks(data))
        .catch((err) => console.error("❌ Error fetching tasks:", err));
    }
  }, [user]);

  const workData = useMemo(() => {
    const map = {};

    tasks.forEach((task) => {
      let sessions = [];
      try {
        sessions = JSON.parse(task.duration || "[]");
      } catch {}

      sessions.forEach((session) => {
        if (!session.start) return;

        const start = new Date(session.start);
        const end = session.end
          ? new Date(session.end)
          : session.pause
          ? new Date(session.pause)
          : new Date();

        const dateStr = start.toISOString().split("T")[0];
        const durationMs = clampSessionToWorkingHours(start, end);
        const hours = parseFloat((durationMs / 3600000).toFixed(2));

        map[dateStr] = (map[dateStr] || 0) + hours;
      });

      // fallback for non-tracked scheduled task
      if ((!sessions || sessions.length === 0) && task.start_date) {
        const dateStr = task.start_date;
        const hours = parseFloat(0 || 0);
        map[dateStr] = (map[dateStr] || 0) + hours;
      }
    });

    // Clamp hours to max 8
    const result = {};
    for (const date in map) {
      result[date] = Math.min(8, parseFloat(map[date].toFixed(2)));
    }

    return result;
  }, [tasks]);

  return workData;
};